import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

interface WorkdayModule {
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface WorkdayMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const WorkdayMenu = ({ isOpen, onClose }: WorkdayMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Group modules by category for better organization
  const moduleCategories = {
    "Core HR & HCM": [
      {
        title: "Workday HCM Core",
        category: "Core HR",
        description: "Employee profiles, organizational management, and workforce planning for comprehensive HR management.",
        features: ["Employee Profiles", "Organizational Management", "Workforce Planning", "Position Management"],
        technologies: ["Workday HCM", "Workday Organizational Management", "Workday Position Management"],
        href: "/workday/hcm-core"
      },
      {
        title: "Workday Payroll",
        category: "Payroll",
        description: "Global payroll processing, tax management, and compensation for worldwide operations.",
        features: ["Global Payroll Processing", "Tax Management", "Compensation Planning", "Payroll Reporting"],
        technologies: ["Workday Payroll", "Workday Tax", "Workday Compensation"],
        href: "/workday/payroll"
      },
      {
        title: "Workday Benefits Administration",
        category: "Benefits",
        description: "Enrollment, eligibility, and life event processing for benefits administration.",
        features: ["Benefits Enrollment", "Eligibility Management", "Life Event Processing", "COBRA Administration"],
        technologies: ["Workday Benefits", "Workday Eligibility", "Workday Life Events"],
        href: "/workday/benefits"
      },
      {
        title: "Workday Compensation",
        category: "Compensation",
        description: "Salary planning, merit increases, and equity management for compensation planning.",
        features: ["Salary Planning", "Merit Increases", "Equity Management", "Bonus Planning", "Compensation Benchmarking", "Total Rewards Statements"],
        technologies: ["Workday Compensation", "Workday Adaptive Planning", "Workday Benchmarking", "Workday Analytics"],
        href: "/workday/compensation"
      }
    ],
    "Talent Management": [
      {
        title: "Workday Talent Management",
        category: "Talent",
        description: "Performance reviews, goal setting, and succession planning for talent development.",
        features: ["Performance Management", "Goal Setting", "Succession Planning", "Career Development"],
        technologies: ["Workday Performance Management", "Workday Goals", "Workday Career Hub"],
        href: "/workday/talent"
      },
      {
        title: "Workday Recruiting",
        category: "Recruiting",
        description: "Applicant tracking, interview scheduling, and offer management for talent acquisition.",
        features: ["Applicant Tracking", "Interview Scheduling", "Offer Management", "Candidate Experience"],
        technologies: ["Workday Recruiting", "Workday Candidate Experience", "Workday Interview"],
        href: "/workday/recruiting"
      },
      {
        title: "Workday Learning",
        category: "Learning",
        description: "Training programs, certifications, and skill development for continuous learning.",
        features: ["Training Programs", "Certification Management", "Skill Development", "Learning Analytics"],
        technologies: ["Workday Learning", "Workday Skills Cloud", "Workday Discovery Board"],
        href: "/workday/learning"
      }
    ],
    "Time & Attendance": [
      {
        title: "Workday Time Tracking",
        category: "Time",
        description: "Time entry, attendance, and absence management for workforce time tracking.",
        features: ["Time Entry", "Attendance Tracking", "Absence Management", "Overtime Management"],
        technologies: ["Workday Time Tracking", "Workday Absence", "Workday Schedule"],
        href: "/workday/time"
      },
      {
        title: "Workday Absence Management",
        category: "Absence",
        description: "Leave requests, policy enforcement, and accrual tracking for absence management.",
        features: ["Leave Requests", "Policy Enforcement", "Accrual Tracking", "FMLA Management"],
        technologies: ["Workday Absence", "Workday Leave Management", "Workday Policy Engine"],
        href: "/workday/absence"
      }
    ],
    "Financial Management": [
      {
        title: "Workday Expenses",
        category: "Expenses",
        description: "Expense reporting, travel management, and expense analytics for expense management.",
        features: ["Expense Reporting", "Mobile Receipt Capture", "Travel Integration", "Automated Approvals", "Expense Analytics", "Compliance Management"],
        technologies: ["Workday Expenses", "Workday Mobile", "Workday Travel", "Workday Analytics"],
        href: "/workday/expenses"
      },
      {
        title: "Workday Procurement",
        category: "Procurement",
        description: "Purchase requisitions, supplier management, and procurement analytics for procurement management.",
        features: ["Purchase Requisitions", "Supplier Management", "Catalog Management", "Contract Management", "Procurement Analytics", "Approval Workflows"],
        technologies: ["Workday Procurement", "Workday Supplier Network", "Workday Analytics", "Workday Mobile"],
        href: "/workday/procurement"
      },
      {
        title: "Workday Inventory",
        category: "Operations",
        description: "Inventory management, stock tracking, and supply chain for operational efficiency.",
        features: ["Inventory Tracking", "Stock Management", "Automated Replenishment", "Supply Chain Visibility", "Inventory Analytics", "Warehouse Management"],
        technologies: ["Workday Inventory", "Workday Supply Chain", "Workday Analytics", "Workday Mobile"],
        href: "/workday/inventory"
      }
    ],
    "Analytics & Reporting": [
      {
        title: "Workday Analytics",
        category: "Analytics",
        description: "HR metrics, predictive analytics, and workforce insights for data-driven decisions.",
        features: ["HR Metrics", "Predictive Analytics", "Workforce Insights", "Custom Dashboards"],
        technologies: ["Workday Prism Analytics", "Workday Discovery Boards", "Workday People Analytics"],
        href: "/workday/analytics"
      },
      {
        title: "Workday Reporting",
        category: "Reporting",
        description: "Custom reports, dashboards, and data visualization for comprehensive reporting.",
        features: ["Custom Reports", "Dashboard Creation", "Data Visualization", "Scheduled Reports"],
        technologies: ["Workday Report Writer", "Workday Discovery Boards", "Workday Prism"],
        href: "/workday/reporting"
      },
      {
        title: "Workday Prism Analytics",
        category: "Analytics",
        description: "External data integration, advanced analytics, and data blending for comprehensive insights.",
        features: ["External Data Integration", "Data Blending", "Advanced Analytics", "Predictive Modeling", "Data Visualization", "Self-Service Analytics"],
        technologies: ["Workday Prism", "Workday Analytics", "Data Integration", "Machine Learning", "Statistical Analysis"],
        href: "/workday/prism"
      }
    ],
    "Planning & Projects": [
      {
        title: "Workday Planning",
        category: "Planning",
        description: "Strategic workforce planning and scenario modeling for future planning.",
        features: ["Workforce Planning", "Scenario Modeling", "Headcount Planning", "Budget Planning"],
        technologies: ["Workday Strategic Sourcing", "Workday Planning", "Workday Adaptive Planning"],
        href: "/workday/planning"
      },
      {
        title: "Workday Projects",
        category: "Projects",
        description: "Project management, resource allocation, and project analytics for project delivery.",
        features: ["Project Management", "Resource Allocation", "Time Tracking", "Budget Management", "Project Analytics", "Collaboration Tools"],
        technologies: ["Workday Projects", "Workday Resource Management", "Workday Time Tracking", "Workday Analytics"],
        href: "/workday/projects"
      }
    ],
    "Platform & Integration": [
      {
        title: "Workday Integration",
        category: "Integration",
        description: "API management, data synchronization, and third-party connections for system integration.",
        features: ["API Management", "Data Synchronization", "Third-party Connections", "Real-time Integration"],
        technologies: ["Workday Integration", "Workday Studio", "Workday Cloud Connect"],
        href: "/workday/integration"
      },
      {
        title: "Workday Extend",
        category: "Platform",
        description: "Custom applications, workflow extensions, and platform development for business solutions.",
        features: ["Custom Applications", "Workflow Extensions", "Low-code Development", "Platform Integration", "Custom Objects", "Business Logic"],
        technologies: ["Workday Extend", "Workday Platform", "Workday APIs", "Workday Orchestrate", "Workday Studio"],
        href: "/workday/extend"
      },
      {
        title: "Workday Mobile",
        category: "Mobile",
        description: "Mobile app access, push notifications, and offline capabilities for mobile workforce.",
        features: ["Mobile App Access", "Push Notifications", "Offline Capabilities", "Mobile Approvals"],
        technologies: ["Workday Mobile", "Workday Push Notifications", "Workday Offline"],
        href: "/workday/mobile"
      }
    ],
    "Security & Education": [
      {
        title: "Workday Security",
        category: "Security",
        description: "Role-based access, audit trails, and compliance reporting for security management.",
        features: ["Role-based Access", "Audit Trails", "Compliance Reporting", "Data Security"],
        technologies: ["Workday Security", "Workday Audit", "Workday Compliance"],
        href: "/workday/security"
      },
      {
        title: "Workday Student",
        category: "Education",
        description: "Student information system, academic records, and student lifecycle for educational institutions.",
        features: ["Student Records", "Academic Planning", "Registration", "Financial Aid", "Student Billing", "Alumni Management"],
        technologies: ["Workday Student", "Workday Academic Planning", "Workday Financial Aid", "Workday Student Billing"],
        href: "/workday/student"
      }
    ]
  };

  const handleModuleClick = (module: WorkdayModule) => {
    navigate(module.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef}
      className="fixed top-16 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border shadow-xl z-[57]"
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Workday Modules & Services</h3>
        <div className="max-h-96 overflow-y-auto space-y-6">
          {Object.entries(moduleCategories).map(([categoryName, modules]) => (
            <div key={categoryName} className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide border-b border-border pb-1">
                {categoryName}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {modules.map((module, index) => (
                  <button
                    key={index}
                    onClick={() => handleModuleClick(module)}
                    className="text-left px-3 py-2 text-sm text-foreground hover:text-orange-600 hover:bg-orange-50/50 transition-colors duration-200 rounded-md"
                  >
                    <span className="font-medium text-xs">{module.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkdayMenu;